/**
 * Where a visitor came from, so a waitlist signup can be credited to the ad, post or site that sent them.
 *
 * On landing we read the campaign tags in the address (utm_source, utm_medium, utm_campaign, utm_content,
 * utm_term), whether it was an ad click (Meta adds fbclid, Google adds gclid; we keep which, not the id) and
 * the outside site that linked here. A visit with none of these (typed address, bookmark, our own pages)
 * changes nothing, so someone who clicks an ad and comes back directly a week later is still credited to the ad.
 *
 * Two touches are kept in localStorage for 90 days: the first that ever brought them, and the latest.
 * Nothing leaves the browser until the visitor submits the waitlist form.
 */

const STORE = 'monet_attribution';
const MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000;
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];

const read = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(STORE) || 'null');
    if (!saved || Date.now() - new Date(saved.first?.at).getTime() > MAX_AGE_MS) return null;
    return saved;
  } catch {
    return null;
  }
};

/** Record this visit's source, if it has one. Call once when the site loads. */
export function captureAttribution() {
  if (typeof window === 'undefined') return;
  try {
    const params = new URLSearchParams(window.location.search);
    const touch = {};
    for (const key of UTM_KEYS) {
      const value = params.get(key);
      if (value) touch[key] = value.trim().toLowerCase().slice(0, 150);
    }
    if (params.has('fbclid')) touch.adClick = 'meta';
    else if (params.has('gclid')) touch.adClick = 'google';

    let referrer = '';
    try {
      const host = document.referrer ? new URL(document.referrer).hostname : '';
      if (host && host.replace(/^www\./, '') !== window.location.hostname.replace(/^www\./, '')) referrer = host;
    } catch { /* unreadable referrer */ }
    if (referrer) touch.referrer = referrer;

    if (!Object.keys(touch).length) return; // direct visit: keep what we have
    touch.landingPage = window.location.pathname;
    touch.at = new Date().toISOString();

    const saved = read();
    localStorage.setItem(STORE, JSON.stringify({ first: saved?.first || touch, last: touch }));
  } catch { /* storage blocked: signup just records no source */ }
}

/** The saved touches plus the page the visitor is on now, to send with a signup. */
export function readAttribution() {
  if (typeof window === 'undefined') return null;
  const saved = read();
  return { first: saved?.first || null, last: saved?.last || null, signupPage: window.location.pathname };
}
